import { Fragment } from 'react'
import ReactMarkdown from 'react-markdown'
import StatBlock from './StatBlock'
import BarChart from './BarChart'
import MeterBar from './MeterBar'
import Chip from './Chip'
import RankedListRow from './RankedListRow'
import InsightComparisonBlock from './InsightComparisonBlock'
import SupportingEvidenceBlock from './SupportingEvidenceBlock'
import { LINK_KINDS } from './linkKinds'
import styles from './BlockRenderer.module.css'

// Inline-only markdown for a prose leaf: bold / italic / code / links render, but
// any block construct (heading, list, blockquote) is unwrapped to its text. The
// backend owns layout via typed blocks, so a prose string carries at most inline
// emphasis, never structure — this is where that rule is enforced on the page.
const INLINE_ELEMENTS = ['strong', 'em', 'code', 'del', 'a', 'p']

function InlineMarkdown({ text }) {
  return (
    <ReactMarkdown allowedElements={INLINE_ELEMENTS} unwrapDisallowed components={{ p: ({ children }) => children }}>
      {text}
    </ReactMarkdown>
  )
}

// One layout segment of a prose block. The backend has already turned the model's
// paragraphs and list markers into JSON shape — a paragraph keeps its line breaks, a
// list arrives as items — so this draws the shape and renders only inline markdown.
function ProseSegment({ segment }) {
  if (segment.kind === 'paragraph') {
    return (
      <p className={styles.proseSegment}>
        {(segment.lines ?? []).map((line, i) => (
          <Fragment key={i}>{i > 0 && <br />}<InlineMarkdown text={line} /></Fragment>
        ))}
      </p>
    )
  }
  const List = segment.kind === 'numbered' ? 'ol' : 'ul'
  return (
    <List className={`${styles.proseSegment} ${styles.proseList}`}>
      {(segment.items ?? []).map((item, i) => <li key={i}><InlineMarkdown text={item} /></li>)}
    </List>
  )
}

const SEVERITY_COLOR = {
  high: 'var(--color-semantic-red)',
  medium: 'var(--color-semantic-yellow)',
  low: 'var(--color-semantic-green)',
}

const TONE_COLOR = {
  good: 'var(--color-semantic-green)',
  warn: 'var(--color-orange-400)',
  bad: 'var(--color-semantic-red)',
  info: 'var(--color-semantic-blue)',
}

/**
 * BlockRenderer — dispatches a generic `blocks` array (see the block schema
 * documented in the plan / mock fixtures) to the right small renderer by
 * `type`. This is the single place that turns data into layout: an insight
 * card's hero body and a detail-modal opportunity's content are both just
 * `<BlockRenderer blocks={...} />` with different data.
 *
 * Unknown block types are skipped rather than throwing, so a payload ahead
 * of the UI's block-type support degrades instead of breaking the page.
 *
 * Props:
 *   blocks      {Array<object>} — each `{ type, heading?, ...fields }`
 *   accentColor {string} — optional; when set (the Home cards, not the
 *                          detail modal), tints numeric values — the hero
 *                          stat, ranked-list values, and bar values — with
 *                          the card's accent instead of the default blue.
 */
export default function BlockRenderer({ blocks = [], accentColor }) {
  return (
    <>
      {blocks.map((block, i) => (
        <div key={i} className={styles.block}>
          {/* callout, comparison, and evidence render their own heading inside their box (see renderBlock) */}
          {block.heading && !['callout', 'comparison', 'evidence'].includes(block.type) && (
            <div className={styles.heading}>{block.heading}</div>
          )}
          {renderBlock(block, accentColor)}
        </div>
      ))}
    </>
  )
}

function renderBlock(block, accentColor) {
  switch (block.type) {
    case 'stat':
      return <StatBlock {...block} accentColor={block.accentColor ?? accentColor} />

    case 'statGrid':
      return (
        <div className={styles.statGrid}>
          {(block.items ?? []).map((item, i) => <StatBlock key={i} {...item} tile />)}
        </div>
      )

    case 'rankedList':
      return (
        <div className={styles.rankedList}>
          {(block.items ?? []).map(item => (
            <RankedListRow key={item.id ?? item.label} rank={item.rank} label={item.label}
              progress={item.progress} value={item.value} badge={item.badge} valueColor={accentColor} />
          ))}
        </div>
      )

    case 'barList':
      return (
        <BarChart
          items={block.items ?? []}
          unit={block.unit ?? '%'}
          barScale={block.barScale}
          color={accentColor}
          valueColor={accentColor}
        />
      )

    case 'severityList':
      return (
        <div className={styles.severityList}>
          {(block.items ?? []).map(item => (
            <div key={item.label} className={styles.severityRow}>
              <span className={styles.severityLabel}>{item.label}</span>
              <span className={styles.severityValue} style={{ color: SEVERITY_COLOR[item.severity] }}>
                {capitalize(item.severity)}
              </span>
            </div>
          ))}
        </div>
      )

    case 'progressRows':
      return (
        <div className={styles.progressRows}>
          {(block.items ?? []).map(item => (
            <MeterBar key={item.label} label={item.label} value={item.value} tone={item.tone}
              caption={item.caption}
              valueColor={accentColor ?? TONE_COLOR[item.tone]}
              color={accentColor} />
          ))}
        </div>
      )

    case 'prose':
      return (
        <div className={styles.prose}>
          {(block.segments ?? []).map((segment, i) => <ProseSegment key={i} segment={segment} />)}
        </div>
      )

    case 'factList':
      return (
        <ul className={styles.bulletList}>
          {(block.items ?? []).map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      )

    case 'keyValue': {
      // `boxed` wraps the whole block in a panel tinted from the accent — only the
      // box is colored, the text keeps its default emphasis. color-mix keeps the fill
      // and border translucent so the accent reads as a wash, not a solid fill.
      const accent = accentColor ?? 'var(--color-semantic-blue)'
      const boxStyle = block.boxed
        ? {
            backgroundColor: `color-mix(in srgb, ${accent} 12%, transparent)`,
            borderColor: `color-mix(in srgb, ${accent} 32%, transparent)`,
          }
        : undefined
      return (
        <div className={`${styles.keyValue} ${block.boxed ? styles.keyValueBoxed : ''}`} style={boxStyle}>
          {(block.rows ?? []).map((row, i) => (
            <div key={i} className={styles.keyValueRow}>
              <span className={styles.keyValueLabel}>{row.label}</span>
              <span className={styles.keyValueValue}>{row.value}</span>
            </div>
          ))}
        </div>
      )
    }

    case 'staticText':
      return <p className={styles.staticText}>{block.text}</p>

    case 'steps':
      return (
        <ol className={styles.steps}>
          {(block.items ?? []).map((item, i) => <li key={i}>{item}</li>)}
        </ol>
      )

    case 'chips':
      return (
        <div className={styles.chips}>
          {(block.items ?? []).map((item, i) => <Chip key={i} label={item.label} tone={item.tone} />)}
        </div>
      )

    case 'linkList': {
      // Each item renders as an interactive element the frontend owns, keyed by
      // link_kind (the backend only whitelists the kind, never the behavior). A kind
      // with no renderer falls back to a plain, non-clickable pill rather than breaking.
      const renderItem = LINK_KINDS[block.link_kind]
      return (
        <div className={styles.chips}>
          {(block.items ?? []).map((item, i) => (
            renderItem
              ? renderItem(item, i)
              : <Chip key={i} label={item.label} tone="info" color={accentColor} />
          ))}
        </div>
      )
    }

    case 'bulletList':
      return (
        <ul className={styles.bulletList}>
          {(block.items ?? []).map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      )

    case 'callout':
      return (
        <div className={styles.callout}>
          {block.heading && <div className={styles.calloutHeading}>{block.heading}</div>}
          <div className={styles.calloutBody}>{block.body}</div>
        </div>
      )

    case 'comparison':
      return <InsightComparisonBlock {...block} />

    case 'evidence':
      return <SupportingEvidenceBlock {...block} accentColor={accentColor} />

    default:
      return null
  }
}

function capitalize(s) {
  return typeof s === 'string' && s.length ? s[0].toUpperCase() + s.slice(1) : s
}
